import { RandomId } from "@swivl-mono/helpers";

import { MemberInterface } from "../Members/Members.Interface";
import { VoiceNodeCommand } from "../Node/Node.Interface";
import { UnitInterface } from "../Unit/Unit.Interface";
import { CountryCode } from "../UnitsAndLocalization/CountryCode";
import { UserInterface } from "../User/User.Interface";
import { ActionTaken, ActionTakenType, From, MessageInterface, MessageType } from "./Message.Interface";
import {
  ActionInterface,
  CarouselPayloadCard,
  CommandPayloadCommandType,
  CreateUserAndStartConversationPayloadInterface,
  LocationPickerLocation,
  LocationPickerPayload,
  MediaPayloadMediaType,
  ProfileCollectDataItem,
  ProfileCollectStatus,
  SpacePickerParams,
  SpacePickerPayload,
  StartConversationPayloadInterface,
  TakeoverEventType,
  TypingPayloadActionType,
  UserShouldMergePayload,
  ValidMessagePayload,
} from "./Message.Types";

export function createBaseMessage(params: {
  from: From;
  orgId: number;
  userId: number;
  type: MessageType;
  payload: ValidMessagePayload;
  memberId?: number;
}): MessageInterface {
  const { type, payload, userId, orgId, from, memberId: agentMemberId } = params;
  return {
    uuid: RandomId(),
    type,
    createdAt: new Date(),
    payload,
    userId,
    orgId,
    from,
    agentMemberId,
  };
}

export function createAuthMessage(from: From, orgId: number, userId: number, token: string): MessageInterface {
  return createBaseMessage({
    from,
    orgId,
    userId,
    type: MessageType.Auth,
    payload: {
      orgId: orgId,
      userId: userId,
      from: from,
      token: token,
    },
  });
}

export function createUserShouldMergePayloadMessage(
  from: From,
  orgId: number,
  userId: number,
  mergeFromUser: UserInterface,
  mergeToUser: UserInterface,
): MessageInterface {
  const payload: UserShouldMergePayload = {
    mergeFromAuth: { userId: mergeFromUser.id, token: mergeFromUser.token! },
    mergeToAuth: { userId: mergeToUser.id, token: mergeToUser.token! },
  }
  return createBaseMessage({
    from,
    orgId,
    userId,
    type: MessageType.UserShouldMerge,
    payload,
  });
}

export function createActionTaken(type: ActionTakenType, target?: string, data?: any): ActionTaken {
  return { type: type, target: target, data: data };
}

export function createActionTakenMessage(from: From, orgId: number, userId: number, messageId: string, actionTaken: ActionTaken): MessageInterface {
  return createBaseMessage({
    from,
    orgId,
    userId,
    type: MessageType.ActionTaken,
    payload: {
      messageId: messageId,
      actionTaken: actionTaken,
    },
  });
}

export function createButtonsMessage(from: From, orgId: number, userId: number, actions: Array<ActionInterface>, text?: string): MessageInterface {
  return createBaseMessage({
    from,
    orgId,
    userId,

    type: MessageType.Buttons,
    payload: {
      actions: actions,
      text: text,
    },
  });
}

export function createCreateUserAndStartConversationMessage(
  from: From,
  orgId: number,
  startOnQuest?: string,
  preloadVariables?: { [key: string]: string },
): MessageInterface {
  let payload: CreateUserAndStartConversationPayloadInterface = {
    orgId: orgId,
    isNewUser: true,
  };
  if (startOnQuest) {
    payload.startOnQuest = startOnQuest;
  }
  if (preloadVariables) {
    payload.preloadVariables = preloadVariables;
  }

  return createBaseMessage({ from, orgId, userId: 0, type: MessageType.CreateUserAndStartConversation, payload });
}

export function createStartConversationMessage(
  from: From,
  orgId: number,
  userId: number,
  startOnQuest?: string,
  preloadVariables?: { [key: string]: string },
  isNewUser?: boolean,
): MessageInterface {
  let payload: StartConversationPayloadInterface = {
    orgId: orgId,
    isNewUser: isNewUser ?? false,
  };
  if (startOnQuest) {
    payload.startOnQuest = startOnQuest;
  }
  if (preloadVariables) {
    payload.preloadVariables = preloadVariables;
  }

  return createBaseMessage({ from, orgId, userId, type: MessageType.StartConversation, payload });
}

export function createTakeoverEventMessage(from: From, orgId: number, userId: number, member: MemberInterface): MessageInterface {
  return createBaseMessage({
    from,
    orgId,
    userId,
    type: MessageType.TakeoverEvent,
    payload: { type: TakeoverEventType.TAKEOVER_STARTED, member },
    memberId: member.id,
  });
}

export function createEndTakeoverEventMessage(from: From, orgId: number, userId: number): MessageInterface {
  return createBaseMessage({
    from,
    orgId,
    userId,
    type: MessageType.TakeoverEvent,
    payload: {
      type: TakeoverEventType.TAKEOVER_ENDED,
    },
  });
}

export function createTextMessage(from: From, orgId: number, userId: number, text: string, openLinksInNewTab: boolean = true): MessageInterface {
  return createBaseMessage({
    from,
    orgId,
    userId,

    type: MessageType.Text,
    payload: {
      text: text,
      openLinksInNewTab: openLinksInNewTab,
    },
  });
}

export function createTypingMessage(from: From, orgId: number, userId: number, action: TypingPayloadActionType): MessageInterface {
  return createBaseMessage({
    from,
    orgId,
    userId,

    type: MessageType.Typing,
    payload: {
      action: action,
    },
  });
}

export function createCarouselPayloadCard(
  id: string,
  actions: Array<ActionInterface>,
  mediaURL?: string,
  title?: string,
  description?: string,
): CarouselPayloadCard {
  return {
    id: id,
    mediaURL: mediaURL,
    title: title,
    description: description,
    actions: actions,
  };
}

export function createCarouselMessage(from: From, orgId: number, userId: number, cards: Array<CarouselPayloadCard>): MessageInterface {
  return createBaseMessage({
    from,
    orgId,
    userId,
    type: MessageType.Carousel,
    payload: {
      cards: cards,
    },
  });
}

export function createCommandMessage(from: From, orgId: number, userId: number, type: CommandPayloadCommandType, payload: any): MessageInterface {
  return createBaseMessage({
    from,
    orgId,
    userId,
    type: MessageType.Command,
    payload: {
      type: type,
      payload: payload,
    },
  });
}

export function createMediaMessage(from: From, orgId: number, userId: number, mediaType: MediaPayloadMediaType, mediaURL: string): MessageInterface {
  return createBaseMessage({
    from,
    orgId,
    userId,
    type: MessageType.Media,
    payload: {
      mediaType: mediaType,
      mediaURL: mediaURL,
    },
  });
}

export function createMultiSelectMessage(from: From, orgId: number, userId: number, actions: Array<ActionInterface>): MessageInterface {
  return createBaseMessage({
    from,
    orgId,
    userId,
    type: MessageType.MultiSelect,
    payload: {
      actions: actions,
    },
  });
}

export function createLocationPickerMessage(
  from: From,
  orgId: number,
  userId: number,
  locations: LocationPickerLocation[],
  currentZip: string,
  searchRadiusInMeters?: number,
  canChangeZip?: boolean,
): MessageInterface {
  let payload: LocationPickerPayload = {
    locations: locations,
    locationSelected: false,
    searchedZipCodes: [],
  };

  if (currentZip) {
    payload.currentZip = currentZip;
    payload.searchedZipCodes.push(currentZip);
  }
  if (canChangeZip) {
    payload.canChangeZip = canChangeZip;
  }
  if (searchRadiusInMeters) {
    payload.searchRadiusInMeters = searchRadiusInMeters;
  }
  return createBaseMessage({
    from,
    orgId,
    userId,
    type: MessageType.LocationPicker,
    payload,
  });
}

export function createSpacePickerMessage(
  from: From,
  orgId: number,
  userId: number,
  spaces: UnitInterface[],
  params: SpacePickerParams,
  location?: LocationPickerLocation,
): MessageInterface {
  let payload: SpacePickerPayload = {
    spaces: spaces,
    params: params,
    location: location,
    selectedSpace: undefined,
  };

  return createBaseMessage({
    from,
    orgId,
    userId,
    type: MessageType.SpacePicker,
    payload,
  });
}

export function createProfileCollectMessage(
  from: From,
  orgId: number,
  userId: number,
  items: ProfileCollectDataItem[],
  actionButtonText: string,
  status: ProfileCollectStatus,
  countryCodes?: CountryCode[],
): MessageInterface {
  return createBaseMessage({
    from,
    orgId,
    userId,
    type: MessageType.ProfileCollect,
    payload: {
      items: items,
      actionButtonText: actionButtonText,
      status: status,
      countryCodes: countryCodes,
    },
  });
}

export function createVoiceMessage(from: From, orgId: number, userId: number, payload: VoiceNodeCommand): MessageInterface {
  return createBaseMessage({ from, orgId, userId, type: MessageType.Voice, payload });
}
